// src/features/patient/care-treatment/utils/highlightText.ts

/**
 * Purpose : Search words marked in a note, without touching the page React
 * owns: plain text is split into parts the note renders itself (a match
 * becomes a <mark>), and a cleaned HTML body is marked as a string before it
 * is set. A word matches when it starts with a search term, any case
 * ("biops" marks "Biopsy").
 *
 * Contents :
 *
 * * function(termPattern) : the regular expression for a set of words
 * * function(splitMatches) : text → [plain, match, plain, …]
 * * function(highlightHtml) : cleaned HTML → the same HTML with matches in <mark>
 */

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// function(termPattern)
/** A word starting with any of the terms; null when there are none. Group 1 is what precedes it, group 2 the word. */
export function termPattern(terms: string[]): RegExp | null {
  const words = terms.map((t) => t.trim()).filter((t) => t.length > 0);
  if (words.length === 0) return null;
  return new RegExp(`(^|[^\\p{L}\\p{N}])((?:${words.map(escapeRegExp).join("|")})[\\p{L}\\p{N}]*)`, "giu");
}

export interface TextPart {
  text: string;
  match: boolean;
}

// function(splitMatches)
/**
 * The text cut at each match, in order; joined back it is the text unchanged.
 *
 * @param text - The text as printed.
 * @param terms - The search words.
 */
export function splitMatches(text: string, terms: string[]): TextPart[] {
  const pattern = termPattern(terms);
  if (!pattern || !text) return [{ text, match: false }];
  const parts: TextPart[] = [];
  let at = 0;
  for (let m = pattern.exec(text); m; m = pattern.exec(text)) {
    const start = m.index + m[1].length;
    if (start > at) parts.push({ text: text.slice(at, start), match: false });
    parts.push({ text: m[2], match: true });
    at = start + m[2].length;
  }
  if (at < text.length) parts.push({ text: text.slice(at), match: false });
  return parts.length > 0 ? parts : [{ text, match: false }];
}

// function(highlightHtml)
/**
 * Mark matches inside already-cleaned HTML: only text is wrapped, tags and
 * attributes are never matched.
 *
 * @param html - Cleaned HTML (sanitizeNoteHtml's output).
 * @param terms - The search words.
 * @param markClass - The mark's class.
 */
export function highlightHtml(html: string, terms: string[], markClass: string): string {
  if (!html || !termPattern(terms) || typeof DOMParser === "undefined") return html;
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, "text/html");
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  const nodes: Text[] = [];
  for (let n = walker.nextNode(); n; n = walker.nextNode()) nodes.push(n as Text);
  for (const node of nodes) {
    const parts = splitMatches(node.data, terms);
    if (!parts.some((p) => p.match)) continue;
    const frag = doc.createDocumentFragment();
    for (const part of parts) {
      if (!part.match) {
        frag.append(part.text);
        continue;
      }
      const mark = doc.createElement("mark");
      mark.className = markClass;
      mark.textContent = part.text;
      frag.append(mark);
    }
    node.replaceWith(frag);
  }
  return doc.body.innerHTML;
}
